// AuthContext.tsx
import { gql, useMutation } from "@apollo/client";
import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from "react";
import { client } from "../App";
import { GET_SELLER_STORE } from "../Routes/boss/api/seller-store";
import { UPDATE_GUEST } from "../Routes/onboarding/location/page";

interface AuthContextProps {
  isLoggedIn: boolean;
  isAdminLoggedIn: boolean;
  isManagerLoggedIn: boolean;
  login: () => void;
  logout: () => void;
  isFirst: boolean;
  doneOnboarding: () => void;
  loginAdmin: () => Promise<void>;
  logoutAdmin: () => void;
  lastPage: string | null;
  changeLastPage: () => void;
  isSellerFirst: boolean;
  doneSellerOnboarding: () => void;
  isAdministratorLoggedIn: boolean;
  loginAdministrator: () => Promise<void>;
  logoutAdministrator: () => void;
  loginManager: () => void;
  logoutManager: () => void;
  isBrandLoggedIn: boolean;
  loginBrand: () => Promise<void>;
  logoutBrand: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const processIsLoggedIn = () => {
  const token = localStorage.getItem("jwt");
  return Boolean(token);
};

const processIsAdminLoggedIn = () => {
  const token = localStorage.getItem("sellerToken");
  return Boolean(token);
};

const processIsManagerLoggedIn = () => {
  const token = localStorage.getItem("managerToken");
  return Boolean(token);
};

const processIsFirst = () => {
  const onboardingDone = localStorage.getItem("onboarding_done");
  return !Boolean(onboardingDone);
};

const processLastPage = () => {
  const lastPage = localStorage.getItem("lastPage");
  return lastPage;
};

const processIsSellerFirst = () => {
  const sellerOnboardingDone = localStorage.getItem("seller_onboarding_done");
  return !Boolean(sellerOnboardingDone);
};

const processIsAdministratorLoggedIn = () => {
  const adminToken = localStorage.getItem("adminToken");
  return Boolean(adminToken);
};

const processIsBrandLoggedIn = () => {
  const brandToken = localStorage.getItem("brandToken");
  return Boolean(brandToken);
};

const CREATE_PUSH_TOKEN = gql`
  mutation CreateToken($data: CreateTokenInput!) {
    createToken(data: $data) {
      token
      createdAt
    }
  }
`;
const SET_TOKEN_TO_USER = gql`
  mutation SetTokenToUser($push_token: String!) {
    setTokenToUser(push_token: $push_token)
  }
`;
const SET_TOKEN_TO_SELLER = gql`
  mutation SetTokenToSeller($push_token: String!) {
    setTokenToSeller(push_token: $push_token)
  }
`;
const SET_UUID_TO_USER = gql`
  mutation updateUserUuid($uuid: String!) {
    updateUserUuid(uuid: $uuid) {
      id
    }
  }
`;
const SET_LAT_LNG_TO_USER = gql`
  mutation updateUserLatAndLng($lat: Float!, $lng: Float!) {
    updateUserLatAndLng(lat: $lat, lng: $lng) {
      id
    }
  }
`;

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(processIsLoggedIn);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(
    processIsAdminLoggedIn
  );
  const [isAdministratorLoggedIn, setIsAdministratorLoggedIn] = useState(
    processIsAdministratorLoggedIn
  );
  const [isManagerLoggedIn, setIsManagerLoggedIn] = useState(
    processIsManagerLoggedIn
  );
  const [isBrandLoggedIn, setIsBrandLoggedIn] = useState(
    processIsBrandLoggedIn
  );
  const [isFirst, setIsFirst] = useState(processIsFirst);
  const [isSellerFirst, setIsSellerFirst] = useState(processIsSellerFirst);
  const [lastPage, setLastPage] = useState<string | "seller" | "client" | null>(
    processLastPage
  );
  const [createToken] = useMutation(CREATE_PUSH_TOKEN);
  const [setTokenToUser] = useMutation(SET_TOKEN_TO_USER);
  const [setTokenToSeller] = useMutation(SET_TOKEN_TO_SELLER);
  const [setUuidToUser] = useMutation(SET_UUID_TO_USER);
  const [setLatlngToUser] = useMutation(SET_LAT_LNG_TO_USER);
  const [updateGuest] = useMutation(UPDATE_GUEST);
  const login = () => {
    // 로그인 로직 구현 (예: 사용자 인증)
    setIsLoggedIn(true);
  };

  const logout = () => {
    // 로그아웃 로직 구현
    setIsLoggedIn(false);
  };

  const loginAdmin = async () => {
    const sellerToken = localStorage.getItem("sellerToken");
    if (sellerToken) {
      const { data } = await client.query({
        query: GET_SELLER_STORE,
        fetchPolicy: "network-only",
      });

      if (
        data?.getSellerStore &&
        data?.getSellerStore?.signupState === "승인"
      ) {
        localStorage.setItem("lastPage", "seller");
        setIsAdminLoggedIn(true);
        return;
      } else if (!data?.getSellerStore) {
        setIsAdminLoggedIn(true);
        alert("매장 등록을 진행해주세요.");
        return;
      } else {
        localStorage.removeItem("sellerToken");
        setIsAdminLoggedIn(false);
      }
    }
  };

  const logoutAdmin = () => {
    // 로그아웃 로직 구현
    setIsAdminLoggedIn(false);
  };

  const changeLastPage = () => {
    const current = localStorage.getItem("lastPage");
    if (current === "seller") {
      localStorage.setItem("lastPage", "client");
      setLastPage("client");
    } else {
      localStorage.setItem("lastPage", "seller");
      setLastPage("seller");
    }
  };

  const doneOnboarding = () => {
    localStorage.setItem("onboarding_done", "true");
    localStorage.setItem("showSourceModal", "true");

    // 온보딩이 끝날 때, 로컬스토리지에 저장해두었던 푸시토큰을 서버에 보내어 DB에 저장한다.
    const token = localStorage.getItem("expo_push_token");
    if (token) {
      createToken({
        variables: {
          data: {
            token,
          },
        },
      });
    }
    setIsFirst(false);
  };

  const doneSellerOnboarding = () => {
    localStorage.setItem("seller_onboarding_done", "true");
    setIsSellerFirst(false);
  };

  const loginAdministrator = async () => {
    setIsAdministratorLoggedIn(true);
  };

  const logoutAdministrator = () => {
    setIsAdministratorLoggedIn(false);
  };

  const loginManager = async () => {
    setIsManagerLoggedIn(true);
  };

  const logoutManager = () => {
    setIsManagerLoggedIn(false);
  };

  const loginBrand = async () => {
    setIsBrandLoggedIn(true);
  };

  const logoutBrand = () => {
    localStorage.removeItem("brandToken");
    setIsBrandLoggedIn(false);
  };

  useEffect(() => {
    const push_token = localStorage.getItem("expo_push_token");
    const jwt = localStorage.getItem("jwt");
    const lat = localStorage.getItem("lat");
    const lng = localStorage.getItem("lng");
    const uuid = localStorage.getItem("mahi_uuid");
    // const isServerSet = localStorage.getItem("isServerSet");
    const current = localStorage.getItem("lastPage");

    if (push_token && current === "seller") {
      setTokenToSeller({
        variables: {
          push_token,
        },
      });
    }
    if (push_token && jwt) {
      //  로그인하고 푸시토큰 했으면 무조건 보내는 건?
      setTokenToUser({
        variables: {
          push_token,
        },
      });
    }
    if (lat && lng) {
      if (jwt) {
        setLatlngToUser({
          variables: {
            lat: Number(lat),
            lng: Number(lng),
          },
        });
      } else if (uuid) {
        updateGuest({
          variables: {
            lat: Number(lat),
            lng: Number(lng),
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const uuid = localStorage.getItem("mahi_uuid");
        const jwt = localStorage.getItem("jwt");
        const sellerToken = localStorage.getItem("sellerToken");

        if (uuid && jwt) {
          await setUuidToUser({ variables: { uuid } });
        }

        if (sellerToken) {
          const { data } = await client.query({
            query: GET_SELLER_STORE,
            fetchPolicy: "network-only",
          });

          if (data?.getSellerStore?.signupState === "승인") {
            setIsAdminLoggedIn(true);
            return;
          }

          // 요청 상태일 경우에는 입점신청 페이지만 허용
          if (
            data?.getSellerStore?.signupState === "요청" &&
            (window.location.pathname === "/boss/signup/store" ||
              window.location.pathname ===
                "/boss/signup/onboarding/reserve-pack" ||
              window.location.pathname === "/boss/signup/reserve-pack/pack" ||
              window.location.pathname === "/boss/signup/reserve-pack/pickup" ||
              window.location.pathname === "/boss/signup/complete")
          ) {
            setIsAdminLoggedIn(true);
            return;
          }

          setIsAdminLoggedIn(false);
        } else {
          setIsAdminLoggedIn(false);
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        setIsAdminLoggedIn(false);
      }
    };

    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        isManagerLoggedIn,
        login,
        logout,
        isFirst,
        doneOnboarding,
        isAdminLoggedIn,
        loginAdmin,
        logoutAdmin,
        lastPage,
        changeLastPage,
        isSellerFirst,
        doneSellerOnboarding,
        isAdministratorLoggedIn,
        loginAdministrator,
        logoutAdministrator,
        loginManager,
        logoutManager,
        isBrandLoggedIn,
        loginBrand,
        logoutBrand,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export { AuthProvider, useAuth };
